import { createContext, useContext } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useLocalStorage } from "./useLocalStorage";
import { Auth } from "aws-amplify";
import { query } from "../services/salesforce";
import { get } from "../api";
import { getAllUrlParams } from "../utils";
import * as AmazonCognitoIdentity from "amazon-cognito-identity-js";

const AuthContext = createContext();

const randomDigits = (number) => {
    const randomValues = new Uint8Array(number);
    window.crypto.getRandomValues(randomValues);
    return Array.from(randomValues).map(intToHex).join("");
};

const intToHex = (number) => {
    return number.toString(16).padStart(2, "0");
};

export const AuthProvider = ({ children, application }) => {
    const [user, setUser] = useLocalStorage("user", null);
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    const redirectLocation = location.state?.path || "/";
    const answerSearchParams = location.state?.searchParams;

    const passwordLessLogin = async (data) => {
        let paramsData = redirectLocation ? getAllUrlParams(redirectLocation) : {};
        let type = paramsData.type ? paramsData.type : "";
        let recordId = paramsData.recordId ? paramsData.recordId : "";
        let email = data.email.toLowerCase();
        let authenticated = await isAuthenticated();

        if (authenticated) {
            navigate("/", {
                replace: true,
            });
            return;
        }
        const params = {
            username: email,
            password: data.password ? data.password : randomDigits(10),
            attributes: {
                name: email,
                email: email,
            },
            validationData: { type: type ? type : "" },
        };
        let hasError = false;
        try {
            await Auth.signUp(params);
        } catch (e) {
            if (e.message.indexOf("Email not found") > -1) {
                hasError = true;
            }
            console.log(e);
        }
        if (!hasError) {
            try {
                const thisUser = await Auth.signIn(email);
                setUser(thisUser);
                let existingSearchParmas = location?.state?.searchParams ? location?.state?.searchParams : {};
                navigate("/answer", {
                    replace: true,
                    state: {
                        path: redirectLocation,
                        searchParams: {
                            simulation: searchParams.get("simulation"),
                            type: type,
                            recordId: recordId,
                            ...existingSearchParmas,
                        },
                    },
                });
                if (data.callback) {
                    data.callback(false);
                }
            } catch (e) {
                console.log(e);
                setTimeout(() => window.location.reload(), 2000);
            }
        } else {
            if (data.callback) {
                data.callback(true);
            }
        }
    };

    const login = async (data) => {
        try {
            const thisUser = await Auth.signIn(data.email, data.password);
            setUser(thisUser);
            navigate(redirectLocation, { replace: true });
        } catch (e) {
            console.log(e);
            setTimeout(() => window.location.reload(), 2000);
        }
    };

    const authenticateUsingToken = async (data) => {
        const AccessToken = new AmazonCognitoIdentity.CognitoAccessToken({
            AccessToken: data.accessToken,
        });

        // create a CognitoIdToken using the response idToken
        const IdToken = new AmazonCognitoIdentity.CognitoIdToken({
            IdToken: data.idToken,
        });

        // create a RefreshToken using the response refreshToken
        const RefreshToken = new AmazonCognitoIdentity.CognitoRefreshToken({
            RefreshToken: data.refreshToken,
        });

        // create a session object with all the tokens
        const sessionData = {
            IdToken: IdToken,
            AccessToken: AccessToken,
            RefreshToken: RefreshToken,
        };

        // create the CognitoUserSession using the sessionData
        const session = new AmazonCognitoIdentity.CognitoUserSession(sessionData);

        // create an object with the UserPoolId and ClientId
        var poolData = {
            UserPoolId: data.userPoolId,
            ClientId: data.clientId,
        };

        // pass the poolData object to CognitoUserPool
        var userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);

        // create an object containing the username and user pool.
        // You can get the username from CognitoAccessToken object
        // we created above.
        var userData = {
            Username: data.username,
            Pool: userPool,
        };

        // create a cognito user using the userData object
        var cognitoUser = new AmazonCognitoIdentity.CognitoUser(userData);

        // set the cognito user session w/ the CognitoUserSession
        cognitoUser.setSignInUserSession(session);
        let user = await Auth.currentSession();
        Auth.currentAuthenticatedUser()
            .then(async (user) => {
                setUser(user);
                await Auth.currentSession();
                await getUserDetailsWithUser(user);
                let userDetails = getUserDetailsNotAsync();
                if (data.redirectUrl) {
                    navigate(data.redirectUrl, { replace: true });
                } else {
                    navigate("/", { replace: true });
                }
            })
            .catch((err) => console.log(err));
    };

    const loginWithToken = async (data) => {
        let isAuthenticatedData = await isAuthenticated();
        if (isAuthenticatedData) {
            let currentUser = await Auth.currentAuthenticatedUser();
            if (currentUser.username !== data.username) {
                await Auth.signOut();
                setUser(null);
                localStorage.clear();
                await authenticateUsingToken(data);
            } else {
                if (data.redirectUrl) {
                    navigate(data.redirectUrl, { replace: true });
                } else {
                    navigate("/", { replace: true });
                }
            }
        } else {
            await authenticateUsingToken(data);
        }
    };

    const answer = async (userInfo, answer, callback) => {
        try {
            let user = userInfo;
            if (user.challengeName === "CUSTOM_CHALLENGE") {
                user = await Auth.sendCustomChallengeAnswer(user, answer);
                await Auth.currentSession();
                let userData = await Auth.currentAuthenticatedUser();
                setUser(userData);
                await getUserDetails();
                let userDetails = getUserDetailsNotAsync();
                if (redirectLocation) {
                    navigate(redirectLocation, { replace: true });
                } else {
                    navigate("/", { replace: true });
                }
            }
        } catch (e) {
            console.log(e);
            if (e === "No current user") {
                callback("Not a valid code. Please try again.");
            } else {
                callback("Not a valid code. Please try again.");
                // if (e.message) {
                //     setTimeout(() => {
                //         navigate("/signout", {
                //             replace: true,
                //             state: {
                //                 path: redirectLocation,
                //                 searchParams: { simulation: searchParams.get("simulation") },
                //             },
                //         });
                //     }, 4 * 1000);
                // }
            }
        }
    };

    const logout = async () => {
        await Auth.signOut();
        setUser(null);
        localStorage.clear();
        navigate("/login" + (location.state?.query ? location.state?.query : ""), {
            replace: true,
            state: { path: location.state?.path },
        });
    };

    const logoutWithoutNavigate = async () => {
        await Auth.signOut();
    };

    const isAuthenticated = async () => {
        try {
            let user = await Auth.currentSession();
            return true;
        } catch {
            return false;
        }
    };

    const getUserDetailsNotAsync = () => {
        let jwtToken = user?.signInUserSession?.accessToken?.jwtToken;
        if (jwtToken) {
            const userData = localStorage.getItem(jwtToken);
            if (userData) {
                return JSON.parse(userData);
            }
        }
        return "";
    };

    const saveCustomAttributes = async (data) => {
        const user = await Auth.currentAuthenticatedUser();
        await Auth.updateUserAttributes(user, data);
    };

    const setUserDetailsNotAsync = (details) => {
        let jwtToken = user?.signInUserSession?.accessToken?.jwtToken;
        if (jwtToken) {
            localStorage.setItem(jwtToken, JSON.stringify(details));
        }
        return "";
    };

    const getUserDetailsWithUser = async (user) => {
        let jwtToken = user.signInUserSession.accessToken.jwtToken;
        if (jwtToken) {
            const userData = localStorage.getItem(jwtToken);
            if (userData) {
                return JSON.parse(userData);
            } else {
                let result;

                result = await get(`contact/getContact`, {
                    emailId: encodeURIComponent(user.username),
                });

                if (result.isSuccess && result.data) {
                    user["contact"] = result.data;
                }
                user["application"] = application;
                let failed = false;
                try {
                    localStorage.setItem(jwtToken, JSON.stringify(user));
                } catch (error) {
                    failed = true;
                    localStorage.clear();
                }

                if (failed) {
                    localStorage.setItem(jwtToken, JSON.stringify(user));
                }

                return user;
            }
        }

        return user;
    };

    const getUserDetails = async () => {
        if (!user) {
            let user = await Auth.currentAuthenticatedUser();
            setUser(user);
        }

        let jwtToken = user.signInUserSession.accessToken.jwtToken;
        if (jwtToken) {
            const userData = localStorage.getItem(jwtToken);
            if (userData) {
                return JSON.parse(userData);
            } else {
                let result;

                result = await get(`contact/getContact`, {
                    emailId: encodeURIComponent(user.username),
                });

                if (result.isSuccess && result.data) {
                    user["contact"] = result.data;
                }
                user["application"] = application;
                let failed = false;
                try {
                    localStorage.setItem(jwtToken, JSON.stringify(user));
                } catch (error) {
                    failed = true;
                    localStorage.clear();
                }

                if (failed) {
                    localStorage.setItem(jwtToken, JSON.stringify(user));
                }

                return user;
            }
        }

        return user;
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                isAuthenticated,
                getUserDetails,
                passwordLessLogin,
                login,
                answer,
                logout,
                getUserDetailsNotAsync,
                setUserDetailsNotAsync,
                logoutWithoutNavigate,
                loginWithToken,
            }}>
            {children}
        </AuthContext.Provider>
    );
};

const useAuth = () => {
    return useContext(AuthContext);
};

export { useAuth };
